import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface CounterProps {
    value: number
    className?: string
    duration?: number
    decimals?: number
    /** Group thousands with this separator, e.g. a space for "9 659". */
    separator?: string
}

/**
 * Deterministic on both server and client, unlike toLocaleString.
 *
 * Shared with the render path below, which is what puts the final number in
 * the server HTML so it is correct without JavaScript and for screen readers.
 */
const format = (value: number, decimals: number, separator?: string): string => {
    const fixed = value.toFixed(decimals)

    if (!separator) {
        return fixed
    }

    const [whole, fraction] = fixed.split('.')
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator)

    return fraction ? `${grouped}.${fraction}` : grouped
}

/**
 * Counts up to the value when the statistic scrolls into view.
 *
 * Everything the tween calls is defined inside the effect, so the callback
 * closes over locals only. A tween outlives the module that created it during
 * a hot update, and if its callback reaches for a binding that update removed,
 * it throws on every frame from GSAP's global ticker and never unregisters.
 * Keeping the closure self-contained means the worst a stale tween can do is
 * write to a node that is already detached, which the isConnected check drops.
 */
export const Counter: FC<CounterProps> = ({
    value,
    className,
    duration = 2,
    decimals = 0,
    separator
}): ReactNode => {
    const root = useRef<HTMLSpanElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            /**
             * Writes through the existing text node rather than reassigning
             * textContent. `textContent = x` drops every child node and builds
             * a fresh one; React rendered that text node and still holds a
             * reference to it, so once it is swapped underneath, the next
             * reconciliation calls insertBefore against a node that is no
             * longer a child of its recorded parent and the tree throws.
             * Setting nodeValue preserves node identity, so React's reference
             * stays valid and a later re-render just overwrites the value.
             */
            const paint = (text: string): void => {
                if (!element.isConnected) {
                    return
                }

                const node = element.firstChild

                if (node && node.nodeType === 3) {
                    node.nodeValue = text

                    return
                }

                element.textContent = text
            }

            const counter = { current: 0 }

            gsap.to(counter, {
                current: value,
                duration,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: element,
                    start: 'top 90%',
                    once: true
                },
                onUpdate: () => {
                    paint(format(counter.current, decimals, separator))
                },
                onComplete: () => {
                    paint(format(value, decimals, separator))
                }
            })
        }, element)

        return () => context.revert()
    }, [value, duration, decimals, separator])

    return (
        <span ref={root} className={cn('tabular-nums', className)}>
            {format(value, decimals, separator)}
        </span>
    )
}
